import React from 'react';
import AutoSizeTitle from '../../../components/autoSizeTitle/AutoSizeTitle';
import IndividualInformHeader from '../../../components/individualInformHeader/individualInformHeader';
import {
    getSelfcare360DimensionTier,
    SELFCARE360_DIMENSION_META,
} from '../../../components/selfcare360/selfcare360Scales';
import { getIpraSummaryDisplayRows } from '../selfcare360InterventionPlan';
import '../../../components/selfcare360/resumeSelfcare360.css';

const DIMENSION_DISPLAY_META = {
    'aprendizaje de incidentes': { code: 'D5', title: 'Aprendizaje de incidentes' },
    'cumplimiento de reglas': { code: 'D3', title: 'Cumplimiento de reglas' },
    'liderazgo operativo': { code: 'D7', title: 'Liderazgo operativo' },
    'planeacion segura': { code: 'D2', title: 'Planeación segura' },
    'participacion del equipo': { code: 'D6', title: 'Participación del equipo' },
    'stop work authority': { code: 'D4', title: 'Stop work' },
    'controles criticos': { code: 'D1', title: 'Controles críticos' },
    'gestion de fatiga': { code: 'D8', title: 'Gestión de fatiga' },
};

const normalizeKey = (value) => {
    if (!value) return '';
    return String(value)
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .replace(/\s+/g, ' ');
};

const toSentenceCase = (value) => {
    if (!value) return '';
    const normalized = String(value).trim().replace(/\s+/g, ' ');
    if (!normalized) return '';
    const lower = normalized.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const formatDecimalEs = (value, decimals = 1) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return '';
    return num.toFixed(decimals).replace('.', ',');
};

const getPriorityLabel = (score) => {
    const value = Number(score);
    if (!Number.isFinite(value)) return '';

    const tier = getSelfcare360DimensionTier(value);
    if (tier.key === 'critical') return 'Muy alta';
    if (tier.key === 'high') return 'Alta';
    if (tier.key === 'moderate') return 'Media';
    return 'Baja';
};

const getClassification = (title, score) => {
    const key = normalizeKey(title);
    const meta = SELFCARE360_DIMENSION_META[key];
    if (meta?.classification) return meta.classification;

    const value = Number(score);
    if (!Number.isFinite(value)) return '';

    const tier = getSelfcare360DimensionTier(value);
    if (tier.key === 'critical') return 'Brecha crítica';
    if (tier.key === 'high') return 'Alta exposición';
    if (tier.key === 'moderate') return 'Vulnerabilidad';
    return 'Fortaleza';
};

const getRiskBadgeStyle = (tierKey) => {
    if (tierKey === 'critical') {
        return {
            backgroundColor: '#FEF2F2',
            borderColor: '#FECACA',
            color: '#991B1B',
        };
    }
    if (tierKey === 'high') {
        return {
            backgroundColor: '#FFF7ED',
            borderColor: '#FED7AA',
            color: '#C2410C',
        };
    }
    if (tierKey === 'moderate') {
        return {
            backgroundColor: '#FFFBEB',
            borderColor: '#FDE68A',
            color: '#B45309',
        };
    }
    if (tierKey === 'low') {
        return {
            backgroundColor: '#ECFDF5',
            borderColor: '#A7F3D0',
            color: '#166534',
        };
    }

    return {
        backgroundColor: '#F1F5F9',
        borderColor: '#CBD5E1',
        color: '#475569',
    };
};

const PageResumeSelfcare360_3 = ({ selectedEvaluation, individualResult, pageNumber, orgLogo }) => {
    const backgroundStyle = {
        backgroundImage: `url('/informs/pagina.png')`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left top',
        backgroundSize: '100% auto',
    };

    const summaryRows = getIpraSummaryDisplayRows(individualResult?.average_by_dimension);

    const tableRows = summaryRows.map((row) => {
        const key = normalizeKey(row.sourceTitle || row.summaryTitle);
        const displayMeta = DIMENSION_DISPLAY_META[key] || {};
        const displayTitle = displayMeta.title || toSentenceCase(row.sourceTitle || row.summaryTitle);
        const tier = row.excludedFromIpra
            ? { key: 'excluded', color: '#94A3B8', label: 'No computa' }
            : getSelfcare360DimensionTier(row.score);

        return {
            code: row.code || displayMeta.code || '',
            title: displayTitle,
            score: row.score,
            weightPercent: row.weightPercent,
            contribution: row.weightedContribution,
            tierKey: tier.key,
            color: tier.color,
            riskLabel: tier.label,
            priority: row.excludedFromIpra ? 'No computa' : getPriorityLabel(row.score),
            classification: row.excludedFromIpra ? 'Excluida por N/O > 50%' : getClassification(row.sourceTitle || row.summaryTitle, row.score),
        };
    });

    // SPEC-IPRA-03: Priorización por IBP = (100 − score) × peso
    const priorities = [...tableRows]
        .filter((r) => r.priority !== 'No computa')
        .map((r) => ({ ...r, ibp: (100 - (r.score ?? 0)) * (r.weightPercent ?? 0) }))
        .sort((a, b) => b.ibp - a.ibp)
        .slice(0, 3)
        .map((r) => `Bajo ${r.title}`);

    // const topActions = [
    //     'Implementar AST obligatorio',
    //     'Rutina semanal de lecciones aprendidas',
    //     'Auditorías mensuales de reglas críticas.',
    // ];

    return (
        <div className="pdf-page internal-page">
            <div className="internal-page-content" style={backgroundStyle}>
                <AutoSizeTitle text={selectedEvaluation.evl_title} />

                <div className="internal-page-info internal-page-info-individual">
                    <IndividualInformHeader individualResult={individualResult} orgLogo={orgLogo} />

                    <div className="selfcare360-resume-title">Resumen por dimensiones</div>

                    <div className="selfcare360-dim-table-wrapper">
                        <div className="selfcare360-dim-table-shell">
                            <table className="selfcare360-dim-table">
                                <colgroup>
                                    <col style={{ width: '43%' }} />
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '8%' }} />
                                    <col style={{ width: '11%' }} />
                                    <col style={{ width: '16%' }} />
                                    <col style={{ width: '12%' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Dimensión</th>
                                        <th>Puntaje</th>
                                        <th>Peso</th>
                                        <th>Aporte</th>
                                        <th>Semáforo</th>
                                        <th>Prioridad</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableRows.map((r) => (
                                        <tr key={r.code || r.title}>
                                            <td className="selfcare360-dim-td-title">
                                                <div className="selfcare360-dim-title-wrap">
                                                    {r.code && <span className="selfcare360-dim-code-badge">{r.code}</span>}
                                                    <div>
                                                        <div className="selfcare360-dim-name">{r.title}</div>
                                                        <div className="selfcare360-dim-subtitle">{r.classification}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="selfcare360-dim-td-num">{formatDecimalEs(r.score)}</td>
                                            <td className="selfcare360-dim-td-num">{formatDecimalEs(r.weightPercent)}%</td>
                                            <td className="selfcare360-dim-td-num">{formatDecimalEs(r.contribution)}</td>
                                            <td className="selfcare360-dim-td-center">
                                                <span
                                                    className="selfcare360-risk-badge"
                                                    style={getRiskBadgeStyle(r.tierKey)}
                                                >
                                                    <span className="selfcare360-traffic-dot" style={{ backgroundColor: r.color }} />
                                                    {r.riskLabel}
                                                </span>
                                            </td>
                                            <td className="selfcare360-dim-td-center selfcare360-dim-td-priority">{r.priority}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="selfcare360-bottom-two">
                        <div className="selfcare360-bottom-box">
                            <div className="selfcare360-bottom-title">
                                <span className="selfcare360-bottom-dot" style={{ backgroundColor: '#e53935' }} />
                                Prioridades (impactan accidentalidad)
                            </div>
                            <ol className="selfcare360-bottom-list">
                                {priorities.map((p) => (
                                    <li key={p}>{p}</li>
                                ))}
                            </ol>
                        </div>

                        {/**
                         * Top 3 Acciones (pendiente de definición final)
                         *
                         * <div className="selfcare360-bottom-box">
                         *   <div className="selfcare360-bottom-title">
                         *     <span className="selfcare360-bottom-dot" style={{ backgroundColor: '#43a047' }} />
                         *     Top 3 Acciones
                         *   </div>
                         *   <ol className="selfcare360-bottom-list">
                         *     {topActions.map((a) => (
                         *       <li key={a}>{a}</li>
                         *     ))}
                         *   </ol>
                         * </div>
                         */}
                    </div>
                </div>

                <div className="internal-page-number">{pageNumber}</div>
            </div>
        </div>
    );
};

export default PageResumeSelfcare360_3;
